// Enhanced notification hook with better structure and error handling
//@ts-nocheck
import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@integration/factory/queryKeys.ts";

import { PaginationResponse } from "@/integration/application/interfaces/base.ts";
import {
  INotificationWebsocket,
  Notifications,
  NotificationsType,
} from "@/integration/application/interfaces/notification.interface.ts";
import {
  optionsFetchAllNotificationUnred,
  optionsFetchInfinitNotifications,
} from "@/config/plugins/notification.query-options.ts";
import ToastContextExports from "@/context/ToastContext.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import useUser from "@integration/user/hooks/useUser.ts";
import useAuth from "@integration/user/hooks/useAuth.ts";
import useTeam from "@integration/team/hooks/useTeam.ts";
import notificationService from "@integration/application/service/notification.service.ts";
import websocketService from "@integration/application/service/websocket.service.ts";
import teamScheduleService from "@integration/team/services/team-schedule.service.ts";
import { IParamsInivteNotification } from "../interfaces/IParamsInivteNotification";

/**
 * Helper function to get a user-friendly notification message based on type
 */
const getNotificationMessage = (notification: INotificationWebsocket): string => {
  // Extract notification type and customize message based on type
  const type = notification.type?.toLowerCase() || '';

  if (type.includes('invite')) {
    return 'Você recebeu um convite para participar de um time';
  } else if (type.includes('message')) {
    return 'Você recebeu uma nova mensagem';
  } else if (type.includes('friend')) {
    return 'Você recebeu uma solicitação de amizade';
  } else if (type.includes('team')) {
    return 'Há uma atualização no seu time';
  } else if (notification.description) {
    // Use the description if available
    return notification.description;
  } else {
    // Default message
    return 'Você recebeu uma nova notificação';
  }
};

/**
 * Enhanced hook for managing notifications with better structure and error handling
 */
const useNotification = () => {
  // Core dependencies
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { userAuth } = useUser();
  const { acceptionInvitation, declineInvitation } = useTeam();
  const { pathMarkAsRead, deleteNotificationById } = notificationService();
  const {updateScheduleCheckinConfirmation} = teamScheduleService();
  const { useNotificationWebSocket } = websocketService();
  const { showToast, showWebSocketToast } = ToastContextExports.useToast();

  // State for WebSocket notifications
  const [wsNotifications, setWsNotifications] = useState<Notifications[]>([]);

  // Track if we've shown a connection error message
  const [hasShownConnectionError, setHasShownConnectionError] = useState(false);

  // Track if we're currently refreshing notifications
  const isRefreshingRef = useRef(false);

  // ===== WEBSOCKET INTEGRATION =====

  /**
   * Refresh all notification queries
   */
  const refreshNotifications = async () => {
    try {
      // Prevent multiple simultaneous refreshes
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;

      // Only refetch the notifications without invalidating all queries
      // This prevents unnecessary refreshes of other components like the toolbar
      await Promise.all([
        notificationsQuery.refetch(),
        unreadNotificationsQuery.refetch()
      ]);
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  };

  /**
   * Debug logger that only logs in development environment
   */
  const debugLog = (message: string, data?: any) => {
    if (import.meta.env.DEV) {
      if (data) {
        console.log(`[Notification Debug] ${message}`, data);
      } else {
        console.log(`[Notification Debug] ${message}`);
      }
    }
  };

  /**
   * Handle incoming WebSocket notifications
   */
  const handleWebSocketMessage = useCallback((data: INotificationWebsocket) => {
    try {

      // Add to local state
      setWsNotifications(prev => {
        // Check if notification already exists to prevent duplicates
        const exists = prev.some(n => n.id === data.id);
        if (exists) {
          debugLog(`Notification already exists, skipping: ${data.id}`);
          return prev;
        }
        debugLog('Adding new notification to state');
        return [data, ...prev];
      });

      // Get a user-friendly message based on notification type
      const message = getNotificationMessage(data);
      debugLog('User-friendly message:', message);

      // Show toast with the custom message
      showWebSocketToast(data.title || 'Nova notificação', message);

      // Update queries
      debugLog('Refreshing notifications from API');
      refreshNotifications();
    } catch (error) {
      console.error('Error handling WebSocket notification:', error);
    }
  }, [showWebSocketToast, refreshNotifications]);

  /**
   * Handle notifications from getNotificationsPlatform
   */
  const handlePlatformNotifications = useCallback((notifications: Notifications[]) => {
    try {
      debugLog('Received platform notifications:', notifications);

      setWsNotifications(notifications);

      refreshNotifications();
    } catch (error) {
      console.error('Error handling platform notifications:', error);
    }
  }, [refreshNotifications]);

  /**
   * Connect to WebSocket for notifications
   */
  const { connectionState } = useNotificationWebSocket(
    userAuth?.id,
    handleWebSocketMessage,
    handlePlatformNotifications
  );

  /**
   * Handle connection state changes
   */
  useEffect(() => {
    // Show a warning toast if connection fails, but only once
    if (connectionState === 'failed' && !hasShownConnectionError) {
      showToast(
        'Problema de conexão',
        'Não foi possível conectar ao serviço de notificações. Algumas notificações podem não aparecer em tempo real.',
        'warning',
        8000
      );
      setHasShownConnectionError(true);
    }

    // Reset the flag if we reconnect successfully
    if (connectionState === 'connected') {
      setHasShownConnectionError(false);
    }
  }, [connectionState, hasShownConnectionError, showToast]);

  // ===== NOTIFICATION QUERIES =====

  /**
   * Query for fetching all notifications with infinite scrolling
   */
  const notificationsQuery = useInfiniteQuery(
    optionsFetchInfinitNotifications()
  );

  /**
   * Function to fetch a specific page of notifications
   */
  const fetchPage = async (page: number) => {
    try {
      // Cancel any ongoing queries
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.NOTIFICATION.list });

      // Fetch the specified page
      const result = await notificationService().getAllNotification(page, 8);

      // Update the query data
      queryClient.setQueryData(
        QUERY_KEYS.NOTIFICATION.list,
        {
          pages: [result],
          pageParams: [page],
        }
      );

      // Refetch to ensure we have the latest data
      await notificationsQuery.refetch();
    } catch (error) {
      console.error('Error fetching specific page:', error);
      showToast('Erro', 'Não foi possível carregar a página de notificações', 'error');
    }
  };

  /**
   * Query for fetching all unread notifications
   */
  const unreadNotificationsQuery = useQuery(
    optionsFetchAllNotificationUnred(isAuthenticated)
  );

  /**
   * Get the number of unread notifications
   */
  const numberOpenNotifications = unreadNotificationsQuery?.data?.length || 0;

  /**
   * Get all notifications (both from API and WebSocket)
   */
  const notifications = () => {
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, ...result } = notificationsQuery;

    // Get notifications from API
    const notificationsList = data?.pages?.flatMap((item) => item.data) || [];

    // Get pagination information from the latest page
    const paginationInfo = data?.pages?.[data.pages.length - 1] || { page: 1, limit: 8, total: 0, totalPage: 1 };

    // Combine WebSocket notifications with API notifications,
    // filtering out duplicates by ID
    const combinedNotifications = [
      ...(wsNotifications || []),
      ...(notificationsList.filter(notification =>
        !wsNotifications.some(wsNotification => wsNotification.id === notification.id)
      ))
    ];

    return {
      data: combinedNotifications,
      pagination: {
        currentPage: paginationInfo.page,
        totalPages: paginationInfo.totalPage,
        totalItems: paginationInfo.total,
        itemsPerPage: paginationInfo.limit
      },
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      ...result
    };
  };

  // ===== NOTIFICATION ACTIONS =====

  // refreshNotifications function moved to the top of the file

  /**
   * Mark a notification as read
   */
  const markAsRead = async (notificationId: string) => {
    try {
      await markAsReadMutation.mutateAsync(notificationId);

      // Update the WebSocket notifications state
      setWsNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, isRead: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showToast('Erro', 'Não foi possível marcar a notificação como lida', 'error');
    }
  };

  /**
   * Mutation for marking a notification as read
   */
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await pathMarkAsRead(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.NOTIFICATION.list });

      const previousData = queryClient.getQueryData<
        InfiniteData<PaginationResponse<Notifications>>
      >(QUERY_KEYS.NOTIFICATION.list);

      if (previousData) {
        queryClient.setQueryData(QUERY_KEYS.NOTIFICATION.list, {
          ...previousData,
          pages: previousData.pages.map((page) => ({
            ...page,
            data: page.data.map((notification) =>
              notification.id === id
                ? { ...notification, isRead: true }
                : notification
            ),
          })),
        });
      }

      return { previousData };
    },
    onError: (err, _variables, context) => {
      console.error('Error in markAsRead mutation:', err);
      if (context?.previousData) {
        queryClient.setQueryData(QUERY_KEYS.NOTIFICATION.list, context.previousData);
      }
    },
    onSettled: () => {
      refreshNotifications();
    },
  });

  /**
   * Delete a notification
   */
  const deleteNotification = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);

      // Update the WebSocket notifications state
      // This part remains the same as we're just filtering by ID
      setWsNotifications(prev => prev.filter(notification => notification.id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
      showToast('Erro', 'Não foi possível excluir a notificação', 'error');
    }
  };

  /**
   * Mutation for deleting a notification
   */
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteNotificationById(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.NOTIFICATION.list });

      const previousData = queryClient.getQueryData<
        InfiniteData<PaginationResponse<Notifications>>
      >(QUERY_KEYS.NOTIFICATION.list);

      if (previousData) {
        queryClient.setQueryData(
          QUERY_KEYS.NOTIFICATION.list,
          {
            ...previousData,
            pages: previousData.pages.map((page) => ({
              ...page,
              data: page.data.filter((notification) => notification.id !== id),
            })),
          }
        );
      }

      return { previousData };
    },
    onError: (err, _variables, context) => {
      console.error('Error in deleteNotification mutation:', err);
      if (context?.previousData) {
        queryClient.setQueryData(QUERY_KEYS.NOTIFICATION.list, context.previousData);
      }
    },
    onSettled: () => {
      refreshNotifications();
    },
  });

  /**
   * Accept an invitation based on context type
   */
  const acceptionInvitationNotification = async (
    params:  IParamsInivteNotification
  ) => {
    try {


      if (params.contextType === NotificationsType.SCHEDULE_INVITE) {

        await updateScheduleCheckinConfirmation(params.teamId, params.scheduleId, params.checkinId, true);
        // Toast is shown by the hook's onSuccess callback
      } else {
        // Default to team invitation
        await acceptionInvitation(params.teamId, params.inviteId);
      }

      await refreshNotifications();
    } catch (error) {
      console.error('Error accepting invitation:', error);
      showToast('Erro', 'Não foi possível aceitar o convite', 'error');
    }
  };

  /**
   * Decline an invitation based on context type
   */
  const declineInvitationNotification = async (
     params:  IParamsInivteNotification
  ) => {
    try {

      // Use the appropriate service based on context type
      if (params.contextType === NotificationsType.SCHEDULE_INVITE) {
        // For schedule invites, use the updateScheduleCheckinConfirmation method
      await updateScheduleCheckinConfirmation(params.teamId, params.scheduleId, params.checkinId, false);
        // Toast is shown by the hook's onSuccess callback
      } else {
        // Default to team invitation
        await declineInvitation(params.teamId, params.inviteId);
        showToast('Sucesso', 'Convite recusado', 'info');
      }

      await refreshNotifications();
    } catch (error) {
      console.error('Error declining invitation:', error);
      showToast('Erro', 'Não foi possível recusar o convite', 'error');
    }
  };

  return {
    // Notification data
    notifications,
    numberOpenNotifications,

    // Notification actions
    markAsRead,
    deleteNotification,
    refreshNotifications,
    fetchPage,

    // Team invitation actions
    acceptionInvitationNotification,
    declineInvitationNotification,

    // WebSocket status
    connectionState,
  };
};

export default useNotification;
